import { QuizAnswerOption, GameVisuals } from "../../../gql/graphql";
import styles from "./QuizSummary.module.scss";

interface QuizSummaryProps {
  totalPoints: number;
  gameVisuals: GameVisuals;
  answers: QuizAnswerOption[];
}

const scoreMessages = [
  {
    threshold: 24,
    title: "You're owning every moment.",
    message: `You're taking positive steps to manage your EoE, and it shows in your daily life. Remember, managing a chronic condition is an ongoing journey, and it's normal to have ups and downs. Keep building on your strengths and celebrate your progress. And don't forget to continue to work with your health care providers to monitor your EoE.\n\nMy EoE Life is here to support you. Explore the site and resources for practical skills and knowledge to help manage your EoE and own every moment.`,
  },
  {
    threshold: 17,
    title: "You're on the right path.",
    message: `You're making meaningful efforts to manage your EoE, and that's something to be proud of. There may be areas where you can find more support or new strategies. Remember, you're not alone—many people with EoE are on a similar path, learning and adjusting as they go. Be sure to reach out to your health care provider to share the results of your quiz and talk about the areas for which you might need more support.\n\nMy EoE Life is here to support you. Explore the site and resources for practical skills and knowledge to help manage your EoE and own every moment.`,
  },
  {
    threshold: 0,
    title: "It's time to get unstuck – together.",
    message: `Managing EoE can be challenging, but realizing you need support and asking for it is a sign of strength. Every person's experience is unique, and it's okay to need extra help or new ideas. You deserve care that works for you and helps you feel your best. Be sure to reach out to your health care provider to share the results of your quiz and talk about the areas for which you might need more support.\n\nMy EoE Life is here to support you. Explore the site and resources for practical skills and knowledge to help manage your EoE and own every moment.`,
  },
];

const getResultMessage = (score: number) => {
  return scoreMessages.find((item) => score >= item.threshold) || scoreMessages[scoreMessages.length - 1];
};

export const QuizSummary = ({ totalPoints, gameVisuals }: QuizSummaryProps) => {
  const result = getResultMessage(totalPoints);

  return (
    <div className={styles.container}>
      <div className={styles.summaryCard} style={{ backgroundColor: gameVisuals.backgroundColor }}>
        <div
          className={styles.header}
          style={{
            backgroundColor: gameVisuals.themeColor,
            color: "white",
          }}
        >
          <h1>Quiz Complete!</h1>
        </div>

        <div className={styles.content}>
          <h2 className={styles.title} style={{ color: gameVisuals.themeColor }}>
            {result.title}
          </h2>

          <p className={styles.message} style={{ color: "#374151" }}>
            {result.message}
          </p>

          <div className={styles.resourcesSection}>
            <h4 className={styles.resourcesTitle} style={{ color: gameVisuals.themeColor }}>
              Explore More Resources:
            </h4>

            <ul className={styles.resourcesList}>
              <li>
                <b>Looking for ways to navigate daily life with EoE?</b> Visit{" "}
                <a
                  href="https://myeoelife.gastro.org/navigating-life-with-eoe"
                  style={{ color: gameVisuals.themeColor }}
                >
                  Navigating Life with EoE
                </a>{" "}
                for tips on routines, diet, travel, and more.
              </li>

              <li>
                <b>Struggling to talk about your EoE with others?</b> Our{" "}
                <a
                  href="https://myeoelife.gastro.org/navigating-life-with-eoe/#conversations"
                  style={{ color: gameVisuals.themeColor }}
                >
                  Conversation Starter resource
                </a>{" "}
                can help you communicate your needs to close friends, coworkers, restaurant workers, and more.
              </li>

              <li>
                <b>Want to understand EoE and how to achieve remission?</b> Check out{" "}
                <a href="https://myeoelife.gastro.org/path-to-remission" style={{ color: gameVisuals.themeColor }}>
                  The Path to Remission
                </a>{" "}
                for clear information on treatment options and goals.
              </li>

              <li>
                <b>Need help preparing for appointments?</b> Use our{" "}
                <a
                  href="https://myeoelife.gastro.org/taking-care-body-mind/#questionsforhcps"
                  style={{ color: gameVisuals.themeColor }}
                >
                  Questions to Ask Your Provider resource
                </a>{" "}
                to make the most of your visits.
              </li>

              <li>
                <b>Looking for emotional support?</b> Visit{" "}
                <a href="https://myeoelife.gastro.org/taking-care-body-mind" style={{ color: gameVisuals.themeColor }}>
                  Taking Care of Body and Mind
                </a>{" "}
                for resources on mental health, stress management, and connecting with others who understand.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
